import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Box, Image, Text, DataTable } from 'grommet';
import NextAnchor from '../../NextAnchor';
import FontAwesomeIcon from '../../FontAwesomeIcon';
import { NotificationsContext } from '../../NotificationsProvider';
import { UnderlinedText } from '../../../utils/styled';
import { isAcceptableFileType } from '../../../utils/files';
import {
    faArrowDownToBracket as IconDownload,
    faChevronLeft as IconPrevious,
    faChevronRight as IconNext,
    faMagnifyingGlassPlus as IconZoomIn,
    faMagnifyingGlassMinus as IconZoomOut,
    faCloudArrowUp as IconUpload,
} from '@fortawesome/pro-light-svg-icons';
import Button from '../../Button';
import { handleErrors } from '../../../utils/errors';
import { Honeybadger } from '@honeybadger-io/react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// File type detection
const FILE_TYPES = {
    IMAGE: 'image',
    PDF: 'pdf',
    HTML: 'html',
    SPREADSHEET: 'spreadsheet',
    TIFF: 'tiff',
    UNKNOWN: 'unknown'
};

const getFileType = (source) => {
    if (!source) return FILE_TYPES.UNKNOWN;
    const ext = source.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return FILE_TYPES.IMAGE;
    if (['tiff', 'tif'].includes(ext)) return FILE_TYPES.TIFF;
    if (['csv', 'xls', 'xlsx'].includes(ext)) return FILE_TYPES.SPREADSHEET;
    if (['htm', 'html'].includes(ext)) return FILE_TYPES.HTML;
    if (ext === 'pdf') return FILE_TYPES.PDF;
    return FILE_TYPES.PDF; // Default to PDF for unknown types
};

const getFileExtension = (source) => source?.split('.').pop()?.toLowerCase();

// Shared container for all preview types
const PreviewContainer = ({ children }) => (
    <Box
        fill
        border={{ size: 'xsmall' }}
        round="xsmall"
        pad="xsmall"
        overflow="hidden"
        style={{ position: 'relative' }}
    >
        {children}
    </Box>
);

// Download link for mobile or unsupported formats
const DownloadLink = ({ source }) => (
    <Box
        align="start"
        round="xsmall"
        pad={{ horizontal: 'small', vertical: 'xsmall' }}
        margin={{ vertical: 'xsmall' }}
        background="neutral-8"
    >
        <NextAnchor
            button
            target="_blank"
            icon={<FontAwesomeIcon size="xsmall" SelectedIcon={IconDownload} />}
            label={<UnderlinedText textDecoration="underline" size="medium">Download remittance advice</UnderlinedText>}
            href={source}
            plain
            download
        />
    </Box>
);

// Image viewer
const ImageViewer = ({ source }) => (
    <Image
        fit="contain"
        src={source}
        alt="Remittance Preview"
        fill
    />
);

// PDF viewer
const PDFViewer = ({ source }) => (
    <embed
        style={{ width: '100%', height: '100%' }}
        type="application/pdf"
        src={source}
        title="PDF viewer"
    />
);

// HTML viewer using sandboxed iframe
const HTMLViewer = ({ source }) => {
    const [resolvedUrl, setResolvedUrl] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        // Fetch HTML content and render via blob URL to avoid redirect/CORS issues
        fetch(source)
            .then(res => {
                if (!res.ok) throw new Error('Failed to load');
                return res.text();
            })
            .then(html => {
                const blob = new Blob([html], { type: 'text/html' });
                setResolvedUrl(URL.createObjectURL(blob));
            })
            .catch(() => setError(true));

        return () => {
            if (resolvedUrl) URL.revokeObjectURL(resolvedUrl);
        };
    }, [source]);

    if (error) {
        return (
            <Box fill align="center" justify="center">
                <Text color="status-error">Unable to load preview</Text>
            </Box>
        );
    }

    if (!resolvedUrl) {
        return <Box fill align="center" justify="center"><Text>Loading...</Text></Box>;
    }

    return (
        <iframe
            src={resolvedUrl}
            sandbox="allow-same-origin"
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Remittance Preview"
        />
    );
};

// Spreadsheet viewer with CSV and Excel support
const SpreadsheetViewer = ({ source, fileType }) => {
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const parseCSV = async (text) => {
            return new Promise((resolve, reject) => {
                Papa.parse(text, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => resolve(results.data),
                    error: (err) => reject(err)
                });
            });
        };

        const parseExcel = async (arrayBuffer) => {
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (!jsonData?.length) return [];

            const headers = jsonData[0];
            return jsonData.slice(1).map(row => {
                const obj = {};
                headers.forEach((header, i) => {
                    obj[header] = row[i] ?? '';
                });
                return obj;
            });
        };

        const buildColumns = (row) => {
            if (!row) return [];
            return Object.keys(row).map(key => ({
                property: key,
                header: <Text size="small" weight="bold" style={{ whiteSpace: 'nowrap' }}>{key}</Text>,
                render: datum => <Text size="small" style={{ whiteSpace: 'nowrap' }}>{String(datum[key] ?? '')}</Text>,
                size: 'small',
            }));
        };

        const fetchAndParse = async () => {
            try {
                setLoading(true);
                const response = await fetch(source);

                let parsedData;
                if (fileType === 'csv') {
                    const text = await response.text();
                    parsedData = await parseCSV(text);
                } else {
                    const arrayBuffer = await response.arrayBuffer();
                    parsedData = await parseExcel(arrayBuffer);
                }

                if (parsedData?.length > 0) {
                    setColumns(buildColumns(parsedData[0]));
                    setData(parsedData);
                }
            } catch (err) {
                setError(err.message || 'Failed to load file');
            } finally {
                setLoading(false);
            }
        };

        fetchAndParse();
    }, [source, fileType]);

    if (loading) {
        return (
            <Box fill align="center" justify="center">
                <Text>Loading...</Text>
            </Box>
        );
    }

    if (error) {
        return (
            <Box fill align="center" justify="center">
                <Text color="status-error">{error}</Text>
            </Box>
        );
    }

    return (
        <Box
            fill
            overflow="auto"
            style={{ flex: 1, minHeight: 0, maxHeight: '100%' }}
        >
            <DataTable
                columns={columns}
                data={data}
                pad="xsmall"
                background={{
                    body: ['invisible', 'light-2']
                }}
                pin
            />
        </Box>
    );
};

// TIFF viewer with multipage support. Decodes each page with UTIF into a PNG
// data URL and renders it declaratively, so there is no global-id lookup or
// unmount-during-fetch race (the source of react-tiff's appendChild crash).
const ReactTIFFViewer = ({ source }) => {
    const [pages, setPages] = useState([]); // one PNG data URL per TIFF page
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
    const [currentPage, setCurrentPage] = useState(1);
    const [zoom, setZoom] = useState(1);
    const panRef = useRef(null);
    const isPanning = useRef(false);
    const panStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

    const totalPages = pages.length;

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        setPages([]);
        setCurrentPage(1);

        (async () => {
            try {
                const res = await fetch(source);
                if (!res.ok) throw new Error(`Failed to load TIFF (${res.status})`);
                const buffer = await res.arrayBuffer();
                const mod = await import('utif');
                const UTIF = mod.default || mod;
                const ifds = UTIF.decode(buffer);
                // Decode every page up front. Remittance TIFFs are small (a
                // handful of pages), so the synchronous cost is negligible; if
                // very large multipage docs show up, switch to decoding lazily
                // per currentPage and keep `buffer`/`ifds` around.
                const dataUrls = ifds.map((ifd) => {
                    UTIF.decodeImage(buffer, ifd);
                    const rgba = UTIF.toRGBA8(ifd);
                    const canvas = document.createElement('canvas');
                    canvas.width = ifd.width;
                    canvas.height = ifd.height;
                    const ctx = canvas.getContext('2d');
                    const imageData = ctx.createImageData(ifd.width, ifd.height);
                    imageData.data.set(rgba);
                    ctx.putImageData(imageData, 0, 0);
                    return canvas.toDataURL('image/png');
                });
                if (cancelled) return;
                setPages(dataUrls);
                setStatus('ready');
            } catch (err) {
                if (!cancelled) {
                    // Keep decode failures observable rather than silently
                    // showing the error state. Report the error, not the source
                    // URL, which may carry a signed token.
                    Honeybadger.notify(err, {
                        name: 'ReactTIFFViewer',
                        message: 'Failed to load or decode TIFF',
                    });
                    setStatus('error');
                }
            }
        })();

        return () => { cancelled = true; };
    }, [source]);

    const zoomIn = useCallback(() => setZoom(z => Math.min(z + 0.25, 3)), []);
    const zoomOut = useCallback(() => setZoom(z => Math.max(z - 0.25, 0.25)), []);

    const handleMouseDown = useCallback((e) => {
        const el = panRef.current;
        if (!el) return;
        isPanning.current = true;
        panStart.current = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: el.scrollLeft,
            scrollTop: el.scrollTop,
        };
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isPanning.current) return;
        const el = panRef.current;
        if (!el) return;
        el.scrollLeft = panStart.current.scrollLeft - (e.clientX - panStart.current.x);
        el.scrollTop = panStart.current.scrollTop - (e.clientY - panStart.current.y);
    }, []);

    const handleMouseUp = useCallback(() => {
        isPanning.current = false;
        const el = panRef.current;
        if (el) {
            el.style.cursor = 'grab';
            el.style.userSelect = '';
        }
    }, []);

    if (status === 'loading') {
        return <Box fill align="center" justify="center"><Text>Loading...</Text></Box>;
    }

    if (status === 'error') {
        return (
            <Box fill align="center" justify="center" gap="xsmall">
                <Text color="status-critical">Unable to display this TIFF.</Text>
                <Button plain href={source} target="_blank" download label="Download instead" />
            </Box>
        );
    }

    return (
        <Box fill>
            <Box
                direction="row"
                align="center"
                background="background-contrast"
                round="xxsmall"
                pad="xxsmall"
                margin={{ bottom: 'xsmall' }}
                flex={{ shrink: 0 }}
                gap="xxsmall"
            >
                {/* Zoom */}
                <Box direction="row" align="center" gap="small" pad={{ horizontal: 'xxsmall' }}>
                    <Button
                        event={{ section: 'tiff_viewer', element: 'zoom_out' }}
                        plain
                        disabled={zoom <= 0.25}
                        onClick={zoomOut}
                        icon={<FontAwesomeIcon SelectedIcon={IconZoomOut} size="xxsmall" />}
                        hoverIndicator
                    />
                    <Text size="small" style={{ minWidth: '32px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</Text>
                    <Button
                        event={{ section: 'tiff_viewer', element: 'zoom_in' }}
                        plain
                        disabled={zoom >= 3}
                        onClick={zoomIn}
                        icon={<FontAwesomeIcon SelectedIcon={IconZoomIn} size="xxsmall" />}
                        hoverIndicator
                    />
                </Box>

                {/* Pagination */}
                <Box width="1px" height="16px" background="border" flex={false} />
                <Box flex direction="row" align="center" justify="center" pad={{ horizontal: 'xxsmall' }}>
                    {totalPages > 1 ? (
                        <>
                            <Button
                                event={{ section: 'tiff_viewer', element: 'prev_page' }}
                                plain
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                icon={<FontAwesomeIcon SelectedIcon={IconPrevious} size="xxsmall" />}
                                hoverIndicator
                            />
                            <Text size="small" margin={{ horizontal: 'xsmall' }}>{currentPage} / {totalPages}</Text>
                            <Button
                                event={{ section: 'tiff_viewer', element: 'next_page' }}
                                plain
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                                icon={<FontAwesomeIcon SelectedIcon={IconNext} size="xxsmall" />}
                                hoverIndicator
                            />
                        </>
                    ) : (
                        <Text size="small" color="text-weak">1 page</Text>
                    )}
                </Box>

                {/* Download */}
                <Box width="1px" height="16px" background="border" flex={false} />
                <Box direction="row" align="center" gap="xsmall" pad={{ horizontal: 'xxsmall' }}>
                    <Button
                        event={{ section: 'tiff_viewer', element: 'download' }}
                        plain
                        href={source}
                        target="_blank"
                        download
                        icon={<FontAwesomeIcon SelectedIcon={IconDownload} size="xxsmall" />}
                        label="Download"
                        hoverIndicator
                    />
                </Box>
            </Box>
            <Box
                ref={panRef}
                flex
                style={{ overflow: 'auto', cursor: zoom > 1 ? 'grab' : 'default' }}
                onMouseDown={zoom > 1 ? handleMouseDown : undefined}
                onMouseMove={zoom > 1 ? handleMouseMove : undefined}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <Box style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                    <Image
                        src={pages[currentPage - 1]}
                        alt={`TIFF page ${currentPage} of ${totalPages}`}
                        style={{ display: 'block', maxWidth: 'none' }}
                        draggable={false}
                    />
                </Box>
            </Box>
        </Box>
    );
};

// Upload dropzone for when no remittance is attached
const UploadDropzone = ({ transactionId, onUploaded }) => {
    const notificationCtx = useContext(NotificationsContext);
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const uploadFile = useCallback(async (file) => {
        if (!isAcceptableFileType(file?.name)) {
            notificationCtx.showNotification({
                message: `Unsupported file format: ${file.name}. Please upload an image, PDF, or spreadsheet.`,
                type: 'error',
            });
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('id', transactionId);
        formData.append('txn_files[0]', file);

        try {
            await fetch(`/api/transactions/files/${transactionId}`, {
                method: 'PUT',
                body: formData,
            }).then(handleErrors);
            if (onUploaded) onUploaded();
        } catch (err) {
            notificationCtx.showNotification(err);
        } finally {
            setIsUploading(false);
        }
    }, [transactionId, onUploaded, notificationCtx]);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) uploadFile(file);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) uploadFile(file);
    };

    return (
        <Box
            fill
            align="center"
            justify="center"
            gap="small"
            pad="medium"
            round="xsmall"
            border={{ style: 'dashed', color: isDragging ? 'brand' : 'border' }}
            background={isDragging ? 'active' : undefined}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{ cursor: 'pointer' }}
            onClick={() => !isUploading && fileInputRef.current?.click()}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.csv,.xls,.xlsx,.html,.tiff,.tif"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
            <FontAwesomeIcon SelectedIcon={IconUpload} size="xlarge" color="text-weak" />
            {isUploading ? (
                <Text color="text-weak" textAlign="center">Uploading...</Text>
            ) : (
                <>
                    <Text weight={500} textAlign="center">Upload remittance advice</Text>
                    <Text size="small" color="text-weak" textAlign="center">
                        Drag and drop or click to browse
                    </Text>
                </>
            )}
        </Box>
    );
};

// Main component
const PreviewPane = ({ size, pdfSource, transactionId, onUploaded, children }) => {
    // No source and no transactionId — nothing to show
    if (!pdfSource && !transactionId) return null;

    // Mobile: no upload dropzone, just download link if source exists
    if (size === 'small') {
        if (pdfSource) return <DownloadLink source={pdfSource} />;
        return null;
    }

    // No source but have transactionId — show upload dropzone
    if (!pdfSource) {
        return (
            <PreviewContainer>
                <UploadDropzone transactionId={transactionId} onUploaded={onUploaded} />
            </PreviewContainer>
        );
    }

    const fileType = getFileType(pdfSource);

    // Render appropriate viewer based on file type
    const renderViewer = () => {
        switch (fileType) {
        case FILE_TYPES.IMAGE:
            return <ImageViewer source={pdfSource} />;
        case FILE_TYPES.TIFF:
            return <ReactTIFFViewer source={pdfSource} />;
        case FILE_TYPES.HTML:
            return <HTMLViewer source={pdfSource} />;
        case FILE_TYPES.SPREADSHEET:
            return <SpreadsheetViewer source={pdfSource} fileType={getFileExtension(pdfSource)} />;
        case FILE_TYPES.PDF:
        default:
            return <PDFViewer source={pdfSource} />;
        }
    };

    return (
        <PreviewContainer>
            <Box flex style={{ overflow: 'hidden', minHeight: 0, position: 'relative' }}>
                <Box style={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
                    {renderViewer()}
                </Box>
            </Box>
            {children}
        </PreviewContainer>
    );
};

export default PreviewPane;
